/**
 * FinancePage — coach-only money hub for the team.
 *
 * 2026-07-11 (batch-B G1 — "Finance restructure"). This used to BE the
 * spare-fee finance dashboard (Apr 2026, Tom_Notes E2). It's now a thin
 * parent shell with an IN-PAGE sub-nav (not a sidebar item — the sidebar
 * still points straight at this same /team/:teamId/finance route,
 * unchanged):
 *
 *   Overview     — split summary: money collected from Spares vs
 *                  Full-Timers (season fees), plus total Expenses.
 *   Spares       — the ORIGINAL finance dashboard, moved verbatim into
 *                  frontend/src/pages/finance/SparesTab.jsx. Gated on
 *                  settings.payment_enabled (see note below).
 *   Full Timers  — NEW. Only shown when settings.season_fee_required
 *                  is true. Read-only season-fee status per full-time
 *                  player (frontend/src/pages/finance/FullTimersTab.jsx).
 *                  G7 will add an actual collection flow on top.
 *   Expenses     — NEW. Reads/writes the mig-099 budget ledger through a
 *                  small dedicated endpoint set that works even for teams
 *                  that never turned on the full Budget module (see
 *                  ExpensesTab.jsx's header comment + backend/src/routes/
 *                  budget.js's finance-expenses routes for why).
 *
 * Gating decisions (documented here since they're easy to lose track of):
 *   • Spares tab: the ORIGINAL FinancePage had NO settings gate at all —
 *     it always rendered for any coach. Per the G1 task spec, we now gate
 *     its CONTENT (not the tab button itself — the tab stays visible so a
 *     coach can find the hint) on settings.payment_enabled. When off, a
 *     hint replaces the dashboard with a shortcut to Team Settings →
 *     Pricing.
 *   • Full Timers tab: hidden entirely (nav item + content) unless
 *     settings.season_fee_required === true — this one has an explicit,
 *     unambiguous boolean in the spec, no fallback needed.
 *   • Expenses tab: always visible for coaches, no gate.
 *
 * We fetch team settings directly here (GET /teams/:teamId) rather than
 * trusting the layout's cached Outlet `activeTeam` — TeamPage.jsx's save
 * handler does NOT call the outlet's loadTeams()/setActiveTeam() after a
 * successful save, so a coach who just flipped payment_enabled or
 * season_fee_required and clicked over to Finance would see stale gating
 * from the cached team. A fresh fetch on mount avoids that.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, Users as UsersIcon, Receipt, Shield, AlertTriangle } from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { PageSpinner } from '../components/Skeleton.jsx';
import SparesTab from './finance/SparesTab.jsx';
import OverviewTab from './finance/OverviewTab.jsx';
import FullTimersTab from './finance/FullTimersTab.jsx';
import ExpensesTab from './finance/ExpensesTab.jsx';

export default function FinancePage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [tab, setTab] = useState('overview');
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setSettingsLoading(true);
    api.get(`/teams/${teamId}`)
      .then(r => { if (!cancelled) setSettings(r.data?.settings || {}); })
      .catch(() => { if (!cancelled) setSettings({}); })
      .finally(() => { if (!cancelled) setSettingsLoading(false); });
    return () => { cancelled = true; };
  }, [teamId]);

  const paymentEnabled    = settings?.payment_enabled === true;
  const seasonFeeRequired = settings?.season_fee_required === true;
  // 2026-07-11 (batch-B G7) — seasonFeeCents/settingsCurrency used to be
  // threaded down into FullTimersTab as props; that tab now fetches its
  // own aggregate (fresher, server-resolved) so these were dropped —
  // see the FullTimersTab render below.

  // If the coach turns Season Fees off while sitting on the Full Timers
  // tab, fall back to Overview rather than rendering an orphaned tab body
  // with no matching nav button. Hooks must run unconditionally (before
  // any early return below), so this lives here rather than after the
  // loading/role gates.
  useEffect(() => {
    if (tab === 'fulltimers' && !seasonFeeRequired) setTab('overview');
  }, [tab, seasonFeeRequired]);

  if (roleLoading || settingsLoading) return <PageSpinner />;
  if (!isCoach) {
    return (
      <div className="p-6">
        <div className="card text-center py-10">
          <Shield size={32} className="mx-auto mb-3" style={{ color: 'var(--puck)' }} />
          <h2 className="font-bold mb-1">Coach-only</h2>
          <p className="text-secondary text-sm">
            The Finance tab is only available to coaches and managers.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview',   label: 'Overview',    icon: TrendingUp },
    { key: 'spares',     label: 'Spares',      icon: DollarSign },
    ...(seasonFeeRequired ? [{ key: 'fulltimers', label: 'Full Timers', icon: UsersIcon }] : []),
    { key: 'expenses',   label: 'Expenses',    icon: Receipt },
  ];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" style={{ paddingBottom: 60 }}>
      <div className="flex items-center gap-3 mb-4">
        <DollarSign size={22} style={{ color: 'var(--ice)' }} />
        <h1 className="font-bold text-2xl" style={{ fontFamily: 'var(--font-display)' }}>
          Finance
        </h1>
      </div>

      {/* ── Parent sub-nav — 2026-07-11 (batch-B G1) ── */}
      <div className="flex gap-1 mb-5 flex-wrap" style={{
        background: 'var(--surface-2)', padding: 4, borderRadius: 12,
      }}>
        {tabs.map(opt => {
          const active = tab === opt.key;
          const Icon = opt.icon;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setTab(opt.key)}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                minWidth: 100,
                background: active ? 'var(--surface)' : 'transparent',
                color: active ? 'var(--ice)' : 'var(--text-2)',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              <Icon size={14} />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          teamId={teamId}
          paymentEnabled={paymentEnabled}
          seasonFeeRequired={seasonFeeRequired}
          onSelectTab={setTab}
        />
      )}

      {tab === 'spares' && (
        paymentEnabled
          ? <SparesTab teamId={teamId} />
          : <SparesDisabledHint teamId={teamId} navigate={navigate} />
      )}

      {tab === 'fulltimers' && seasonFeeRequired && (
        // 2026-07-11 (batch-B G7) — FullTimersTab now fetches its own
        // aggregate (GET .../finance/season-fees), which reads
        // season_fee_cents/currency straight from the team's CURRENT
        // settings server-side. Passing seasonFeeCents/currency props
        // here would just be a second, potentially-stale source of the
        // same numbers, so we stopped — teamId is all it needs.
        <FullTimersTab teamId={teamId} />
      )}

      {tab === 'expenses' && (
        <ExpensesTab teamId={teamId} />
      )}
    </div>
  );
}

// Spares tab content-level gate — per G1 spec: the original FinancePage
// had no gate at all, so we add one on payment_enabled specifically (not
// spare_fee_cents/tracking-only mode, which the original page also
// supported) rather than hide the tab itself, so a coach can still find
// the "how do I turn this on" hint from inside Finance.
function SparesDisabledHint({ teamId, navigate }) {
  return (
    <div className="card text-center py-10">
      <AlertTriangle size={28} className="mx-auto mb-3" style={{ color: 'var(--puck)' }} />
      <h2 className="font-bold mb-1">Spare payment collection is off</h2>
      <p className="text-secondary text-sm mb-4" style={{ maxWidth: 420, margin: '0 auto 16px', lineHeight: 1.5 }}>
        Turn on Payments in Team Settings → Pricing to track and collect spare fees here.
      </p>
      <button type="button" className="btn-ice" onClick={() => navigate(`/team/${teamId}/settings/pricing`)}>
        Go to Pricing
      </button>
    </div>
  );
}
